import { useEffect } from 'react';
import { useRotationStore } from 'src/context/RotationContext';
import { useStowageSelectionStore } from 'src/context/StowageSelectionContext';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { StowageRuleViolationStatus } from 'src/enums/stowage/stowageRule/stowageRuleViolationStatus';
import { Violation } from 'src/types/ViewModel/Stowage/Violation/Violation';

interface FilterViolationFunctions {
    filterAllViolations: () => void;
    filterTankViolations: () => void;
    filterCallViolations: () => void;
    filterCargoViolations: () => void;
}

const useUpdatedFilteredViolations = (): FilterViolationFunctions => {
    const { violations } = useViolationsStore();
    const {
        selectedViolationListFilter,
        selectedViolationListStatusFilter,
        selectedTanks,
        setFilteredViolations,
        selectedCargoOperation
    } = useStowageSelectionStore();
    const {
        selectedStowageRotationPortCall,
        selectedStowageRotationBerthCall
    } = useRotationStore();

    /**
     * Filters violations based on their status
     */
    const filterViolationsByStatus = (violations: Violation[]): Violation[] => {
        if (selectedViolationListStatusFilter === 'All statuses') {
            return violations;
        }

        return selectedViolationListStatusFilter === 'Accepted'
            ? violations.filter((violation) => violation.status === StowageRuleViolationStatus.Accepted)
            : violations.filter(
                (violation) =>
                    violation.status === StowageRuleViolationStatus.New ||
                    violation.status === StowageRuleViolationStatus.NotApplicable
            );
    };

    /**
     * Filters violations for all cases
     */
    const filterAllViolations = (): void => {
        setFilteredViolations(filterViolationsByStatus(violations));
    };

    /**
     * Filters violations related to selected tanks
     */
    const filterTankViolations = (): void => {
        if (selectedTanks?.length > 0) {

            if (selectedStowageRotationBerthCall) {
                setFilteredViolations(
                    filterViolationsByStatus(
                        violations.filter(
                            (violation) => selectedTanks.some((tank) => tank.tankId === violation.tankId && violation.berthCallGuid === selectedStowageRotationBerthCall.berthCallGuid)
                        )
                    )
                );
            } else if (selectedStowageRotationPortCall) {
                setFilteredViolations(
                    filterViolationsByStatus(
                        violations.filter(
                            (violation) => selectedTanks.some((tank) => tank.tankId === violation.tankId && violation.portCallGuid === selectedStowageRotationPortCall.portCallGuid)
                        )

                    )
                );
            }
        } else {
            setFilteredViolations([]);
        }
    };

    /**
     * Filters violations related to the selected port call or berth call
     */
    const filterCallViolations = (): void => {
        if (selectedStowageRotationBerthCall) {
            setFilteredViolations(
                filterViolationsByStatus(
                    violations.filter(
                        (violation) => violation.berthCallGuid === selectedStowageRotationBerthCall.berthCallGuid
                    )
                )
            );
        } else if (selectedStowageRotationPortCall) {
            setFilteredViolations(
                filterViolationsByStatus(
                    violations.filter(
                        (violation) => violation.portCallGuid === selectedStowageRotationPortCall.portCallGuid
                    )
                )
            );
        } else {
            setFilteredViolations([]);
        }
    };

    /**
     * Filters violations related to the selected cargo operation
     */
    const filterCargoViolations = (): void => {
        if (selectedCargoOperation) {
            setFilteredViolations(
                filterViolationsByStatus(
                    violations.filter(
                        (violation) => violation.cargoGuid === selectedCargoOperation.cargoGuid
                    )
                )
            );
        } else {
            setFilteredViolations([]);
        }
    };

    /**
     * Apply filters based on current selection criteria
     */
    const applyViolationFilters = (): void => {
        if (!violations || !selectedViolationListFilter || !selectedViolationListStatusFilter) {
            return;
        }

        switch (selectedViolationListFilter) {
            case 'All':
                filterAllViolations();
                break;
            case 'Selected call':
                filterCallViolations();
                break;
            case 'Selected tank':
                filterTankViolations();
                break;
            case 'Selected cargo':
                filterCargoViolations();
                break;
            default:
                setFilteredViolations([]);
                break;
        }
    };

    useEffect(() => {
        applyViolationFilters();
    }, [
        violations,
        selectedViolationListFilter,
        selectedViolationListStatusFilter,
        selectedStowageRotationPortCall,
        selectedStowageRotationBerthCall,
    ]);

    return {
        filterAllViolations,
        filterTankViolations,
        filterCallViolations,
        filterCargoViolations
    };
};

export default useUpdatedFilteredViolations;
